import { useState, useEffect, useCallback } from 'react';
import type { Product } from '../types';
import { getApiUrl } from '../config/api';

const BRANDS = [
  { value: '', label: 'All Brands (shared)' },
  { value: 'copierfinder', label: 'Copier Finder' },
  { value: 'printdealfinder', label: 'Print Deal Finder' },
  { value: 'officeprinters', label: 'Office Printers UK' },
  { value: 'lingscopiers', label: "Ling's Copiers" },
];

const PRODUCT_TYPES = [
  { value: 'mfp', label: 'Multifunction (MFP)' },
  { value: 'printer', label: 'Printer' },
  { value: 'copier', label: 'Copier' },
];

const FORMATS = [
  { value: '', label: 'Any' },
  { value: 'A4', label: 'A4' },
  { value: 'A3', label: 'A3' },
];

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

// ─── Product form ────────────────────────────────────────────────────────────

interface ProductFormState {
  brandId: string;
  manufacturer: string;
  manufacturerSlug: string;
  modelName: string;
  slug: string;
  shortDescription: string;
  description: string;
  features: string[];
  specifications: [string, string][];
  primaryImageUrl: string;
  productType: string;
  format: string;
  colourCapable: boolean;
  priceGuideLease: string;
  priceGuidePurchase: string;
  typicalMonoCpc: string;
  typicalColourCpc: string;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  sourceUrl: string;
  published: boolean;
}

const emptyForm: ProductFormState = {
  brandId: '',
  manufacturer: '',
  manufacturerSlug: '',
  modelName: '',
  slug: '',
  shortDescription: '',
  description: '',
  features: [],
  specifications: [],
  primaryImageUrl: '',
  productType: 'mfp',
  format: 'A4',
  colourCapable: true,
  priceGuideLease: '',
  priceGuidePurchase: '',
  typicalMonoCpc: '',
  typicalColourCpc: '',
  metaTitle: '',
  metaDescription: '',
  metaKeywords: '',
  sourceUrl: '',
  published: false,
};

interface ProductPanelProps {
  product: Product | null;
  onSaved: () => void;
  onClose: () => void;
}

function ProductPanel({ product, onSaved, onClose }: ProductPanelProps) {
  const isNew = product === null;

  const parseForm = (p: Product): ProductFormState => {
    let features: string[] = [];
    let specifications: [string, string][] = [];
    try { features = p.features ? JSON.parse(p.features) : []; } catch { /* empty */ }
    try {
      const specObj = p.specifications ? JSON.parse(p.specifications) : {};
      specifications = Object.entries(specObj) as [string, string][];
    } catch { /* empty */ }

    return {
      brandId: p.brand_id ?? '',
      manufacturer: p.manufacturer,
      manufacturerSlug: p.manufacturer_slug,
      modelName: p.model_name,
      slug: p.slug,
      shortDescription: p.short_description ?? '',
      description: p.description ?? '',
      features,
      specifications,
      primaryImageUrl: p.primary_image_url ?? '',
      productType: p.product_type,
      format: p.format ?? 'A4',
      colourCapable: p.colour_capable === 1,
      priceGuideLease: p.price_guide_lease ?? '',
      priceGuidePurchase: p.price_guide_purchase ?? '',
      typicalMonoCpc: p.typical_mono_cpc ?? '',
      typicalColourCpc: p.typical_colour_cpc ?? '',
      metaTitle: p.meta_title ?? '',
      metaDescription: p.meta_description ?? '',
      metaKeywords: p.meta_keywords ?? '',
      sourceUrl: p.source_url ?? '',
      published: p.published === 1,
    };
  };

  const [form, setForm] = useState<ProductFormState>(product ? parseForm(product) : { ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [researching, setResearching] = useState(false);
  const [slugManual, setSlugManual] = useState(!isNew);

  const set = (field: keyof ProductFormState, value: any) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const updateSlugFromName = (manufacturer: string, model: string) => {
    if (!slugManual) {
      set('slug', slugify(`${manufacturer} ${model}`));
    }
  };

  const handleManufacturerChange = (value: string) => {
    set('manufacturer', value);
    set('manufacturerSlug', slugify(value));
    updateSlugFromName(value, form.modelName);
  };

  const handleModelChange = (value: string) => {
    set('modelName', value);
    updateSlugFromName(form.manufacturer, value);
  };

  const handleAiResearch = async () => {
    if (!form.sourceUrl.trim()) {
      setError('Enter a manufacturer URL first.');
      return;
    }
    setResearching(true);
    setError('');
    try {
      const res = await fetch(getApiUrl('/api/products/ai-research'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url: form.sourceUrl }),
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.message || data.error || 'AI research failed');
        return;
      }

      const data = await res.json();
      const p = data.product;

      let features: string[] = [];
      if (Array.isArray(p.features)) features = p.features;
      else if (typeof p.features === 'string') { try { features = JSON.parse(p.features); } catch { /* empty */ } }

      let specifications: [string, string][] = [];
      if (p.specifications && typeof p.specifications === 'object' && !Array.isArray(p.specifications)) {
        specifications = Object.entries(p.specifications) as [string, string][];
      }

      setForm((prev) => ({
        ...prev,
        manufacturer: p.manufacturer || prev.manufacturer,
        manufacturerSlug: p.manufacturer_slug || slugify(p.manufacturer || prev.manufacturer),
        modelName: p.model_name || prev.modelName,
        slug: p.slug || prev.slug,
        shortDescription: p.short_description || prev.shortDescription,
        description: p.description || prev.description,
        features: features.length ? features : prev.features,
        specifications: specifications.length ? specifications : prev.specifications,
        primaryImageUrl: p.primary_image_url || prev.primaryImageUrl,
        productType: p.product_type || prev.productType,
        format: p.format || prev.format,
        colourCapable: p.colour_capable !== undefined ? Boolean(p.colour_capable) : prev.colourCapable,
        priceGuideLease: p.price_guide_lease || prev.priceGuideLease,
        priceGuidePurchase: p.price_guide_purchase || prev.priceGuidePurchase,
        typicalMonoCpc: p.typical_mono_cpc || prev.typicalMonoCpc,
        typicalColourCpc: p.typical_colour_cpc || prev.typicalColourCpc,
        metaTitle: p.meta_title || prev.metaTitle,
        metaDescription: p.meta_description || prev.metaDescription,
        metaKeywords: p.meta_keywords || prev.metaKeywords,
      }));
      setSlugManual(true);
    } catch {
      setError('Network error during AI research.');
    } finally {
      setResearching(false);
    }
  };

  const handleSave = async () => {
    if (!form.manufacturer.trim() || !form.modelName.trim() || !form.slug.trim()) {
      setError('Manufacturer, model name, and slug are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const specsObj: Record<string, string> = {};
      for (const [k, v] of form.specifications) {
        if (k.trim()) specsObj[k.trim()] = v;
      }

      const body = {
        brandId: form.brandId || null,
        manufacturer: form.manufacturer,
        manufacturerSlug: form.manufacturerSlug,
        modelName: form.modelName,
        slug: form.slug,
        shortDescription: form.shortDescription || null,
        description: form.description || null,
        features: JSON.stringify(form.features.filter(Boolean)),
        specifications: JSON.stringify(specsObj),
        primaryImageUrl: form.primaryImageUrl || null,
        productType: form.productType,
        format: form.format || null,
        colourCapable: form.colourCapable,
        priceGuideLease: form.priceGuideLease || null,
        priceGuidePurchase: form.priceGuidePurchase || null,
        typicalMonoCpc: form.typicalMonoCpc || null,
        typicalColourCpc: form.typicalColourCpc || null,
        metaTitle: form.metaTitle || null,
        metaDescription: form.metaDescription || null,
        metaKeywords: form.metaKeywords || null,
        sourceUrl: form.sourceUrl || null,
        aiResearchedAt: researching ? new Date().toISOString() : undefined,
        published: form.published,
      };

      const url = isNew
        ? getApiUrl('/api/products')
        : getApiUrl(`/api/products/${product!.id}`);

      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.error || 'Failed to save product');
        return;
      }
      onSaved();
    } catch {
      setError('Network error — please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!product) return;
    if (!window.confirm('Delete this product?')) return;
    await fetch(getApiUrl(`/api/products/${product.id}`), {
      method: 'DELETE',
      credentials: 'include',
    });
    onSaved();
  };

  const addFeature = () => set('features', [...form.features, '']);
  const removeFeature = (i: number) => set('features', form.features.filter((_, idx) => idx !== i));
  const updateFeature = (i: number, val: string) =>
    set('features', form.features.map((f, idx) => (idx === i ? val : f)));

  const addSpec = () => set('specifications', [...form.specifications, ['', ''] as [string, string]]);
  const removeSpec = (i: number) => set('specifications', form.specifications.filter((_, idx) => idx !== i));
  const updateSpec = (i: number, field: 0 | 1, val: string) =>
    set(
      'specifications',
      form.specifications.map((s, idx) => {
        if (idx !== i) return s;
        const copy: [string, string] = [...s];
        copy[field] = val;
        return copy;
      }),
    );

  const inputClass = 'w-full px-3 py-2 bg-dark-bg border border-dark-border rounded-lg text-dark-text text-sm focus:outline-none focus:border-blue-500';

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-6 py-4 border-b border-dark-border flex-shrink-0">
        <h2 className="text-lg font-semibold text-white">
          {isNew ? 'New Product' : 'Edit Product'}
        </h2>
        <button onClick={onClose} className="text-dark-text-secondary hover:text-white transition" aria-label="Close">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">

        {/* AI Research */}
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 space-y-3">
          <p className="text-sm font-semibold text-blue-400">AI Product Research</p>
          <p className="text-xs text-dark-text-secondary">
            Paste a manufacturer product page URL and let AI populate all fields automatically.
          </p>
          <input
            type="url"
            value={form.sourceUrl}
            onChange={(e) => set('sourceUrl', e.target.value)}
            placeholder="https://www.lexmark.com/en_gb/printer/..."
            className={inputClass}
          />
          <button
            onClick={handleAiResearch}
            disabled={researching || !form.sourceUrl.trim()}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition disabled:opacity-50 flex items-center gap-2"
          >
            {researching ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Researching...
              </>
            ) : (
              'Research with AI'
            )}
          </button>
        </div>

        {/* Brand */}
        <div>
          <label className="block text-sm font-medium text-dark-text mb-1">Brand</label>
          <select value={form.brandId} onChange={(e) => set('brandId', e.target.value)} className={inputClass}>
            {BRANDS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select>
        </div>

        {/* Manufacturer & Model */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-dark-text mb-1">Manufacturer <span className="text-red-400">*</span></label>
            <input type="text" value={form.manufacturer} onChange={(e) => handleManufacturerChange(e.target.value)} placeholder="Lexmark" className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-dark-text mb-1">Model Name <span className="text-red-400">*</span></label>
            <input type="text" value={form.modelName} onChange={(e) => handleModelChange(e.target.value)} placeholder="XC2326" className={inputClass} />
          </div>
        </div>

        {/* Slug */}
        <div>
          <label className="block text-sm font-medium text-dark-text mb-1">URL Slug <span className="text-red-400">*</span></label>
          <input
            type="text"
            value={form.slug}
            onChange={(e) => { setSlugManual(true); set('slug', e.target.value); }}
            placeholder="lexmark-xc2326"
            className={`${inputClass} font-mono`}
          />
          <p className="text-xs text-dark-text-secondary mt-1">
            /products/{form.manufacturerSlug || 'brand'}/<span className="text-blue-400">{form.slug || 'slug'}</span>
          </p>
        </div>

        {/* Type, Format, Colour */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium text-dark-text mb-1">Type</label>
            <select value={form.productType} onChange={(e) => set('productType', e.target.value)} className={inputClass}>
              {PRODUCT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-dark-text mb-1">Format</label>
            <select value={form.format} onChange={(e) => set('format', e.target.value)} className={inputClass}>
              {FORMATS.filter(f => f.value).map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>
          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.colourCapable} onChange={(e) => set('colourCapable', e.target.checked)} className="rounded" />
              <span className="text-sm text-dark-text">Colour</span>
            </label>
          </div>
        </div>

        {/* Short Description */}
        <div>
          <label className="block text-sm font-medium text-dark-text mb-1">Short Description</label>
          <textarea value={form.shortDescription} onChange={(e) => set('shortDescription', e.target.value)} rows={2} placeholder="A brief 1-2 sentence summary..." className={`${inputClass} resize-y`} />
        </div>

        {/* Full Description */}
        <div>
          <label className="block text-sm font-medium text-dark-text mb-1">Full Description (HTML)</label>
          <textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={8} placeholder="<h2>Overview</h2><p>...</p>" className={`${inputClass} font-mono resize-y`} />
        </div>

        {/* Features */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-dark-text">Features</label>
            <button onClick={addFeature} className="text-xs text-blue-400 hover:text-blue-300">+ Add</button>
          </div>
          <div className="space-y-2">
            {form.features.map((f, i) => (
              <div key={i} className="flex gap-2">
                <input type="text" value={f} onChange={(e) => updateFeature(i, e.target.value)} placeholder="Print speed up to 26 ppm" className={`${inputClass} flex-1`} />
                <button onClick={() => removeFeature(i)} className="text-red-400 hover:text-red-300 px-2 text-sm">x</button>
              </div>
            ))}
          </div>
        </div>

        {/* Specifications */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-dark-text">Specifications</label>
            <button onClick={addSpec} className="text-xs text-blue-400 hover:text-blue-300">+ Add</button>
          </div>
          <div className="space-y-2">
            {form.specifications.map((s, i) => (
              <div key={i} className="flex gap-2">
                <input type="text" value={s[0]} onChange={(e) => updateSpec(i, 0, e.target.value)} placeholder="Print Speed" className={`${inputClass} w-1/3`} />
                <input type="text" value={s[1]} onChange={(e) => updateSpec(i, 1, e.target.value)} placeholder="26 ppm" className={`${inputClass} flex-1`} />
                <button onClick={() => removeSpec(i)} className="text-red-400 hover:text-red-300 px-2 text-sm">x</button>
              </div>
            ))}
          </div>
        </div>

        {/* Primary Image */}
        <div>
          <label className="block text-sm font-medium text-dark-text mb-1">Primary Image URL</label>
          <input type="url" value={form.primaryImageUrl} onChange={(e) => set('primaryImageUrl', e.target.value)} placeholder="https://..." className={inputClass} />
          {form.primaryImageUrl && (
            <img src={form.primaryImageUrl} alt="Preview" className="mt-2 h-24 object-contain rounded bg-white p-2" />
          )}
        </div>

        {/* Pricing */}
        <details className="group">
          <summary className="text-sm font-medium text-dark-text cursor-pointer select-none flex items-center gap-2">
            <svg className="w-4 h-4 text-dark-text-secondary transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            Pricing Guide
          </summary>
          <div className="mt-3 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-dark-text-secondary mb-1">Lease Guide</label>
                <input type="text" value={form.priceGuideLease} onChange={(e) => set('priceGuideLease', e.target.value)} placeholder="From £45/month" className={inputClass} />
              </div>
              <div>
                <label className="block text-xs font-medium text-dark-text-secondary mb-1">Purchase Guide</label>
                <input type="text" value={form.priceGuidePurchase} onChange={(e) => set('priceGuidePurchase', e.target.value)} placeholder="From £2,500" className={inputClass} />
              </div>
              <div>
                <label className="block text-xs font-medium text-dark-text-secondary mb-1">Mono CPC</label>
                <input type="text" value={form.typicalMonoCpc} onChange={(e) => set('typicalMonoCpc', e.target.value)} placeholder="£0.005" className={inputClass} />
              </div>
              <div>
                <label className="block text-xs font-medium text-dark-text-secondary mb-1">Colour CPC</label>
                <input type="text" value={form.typicalColourCpc} onChange={(e) => set('typicalColourCpc', e.target.value)} placeholder="£0.045" className={inputClass} />
              </div>
            </div>
          </div>
        </details>

        {/* SEO */}
        <details className="group">
          <summary className="text-sm font-medium text-dark-text cursor-pointer select-none flex items-center gap-2">
            <svg className="w-4 h-4 text-dark-text-secondary transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            SEO Metadata
          </summary>
          <div className="mt-3 space-y-3">
            <div>
              <label className="block text-xs font-medium text-dark-text-secondary mb-1">Meta Title</label>
              <input type="text" value={form.metaTitle} onChange={(e) => set('metaTitle', e.target.value)} placeholder="Lexmark XC2326 | Lease & Buy" className={inputClass} />
              <p className="text-xs text-dark-text-secondary mt-1">{form.metaTitle.length}/60 characters</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-dark-text-secondary mb-1">Meta Description</label>
              <textarea value={form.metaDescription} onChange={(e) => set('metaDescription', e.target.value)} rows={2} placeholder="150-160 chars..." className={`${inputClass} resize-y`} />
              <p className="text-xs text-dark-text-secondary mt-1">{form.metaDescription.length}/160 characters</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-dark-text-secondary mb-1">Meta Keywords</label>
              <input type="text" value={form.metaKeywords} onChange={(e) => set('metaKeywords', e.target.value)} placeholder="lexmark xc2326, lexmark printer, ..." className={inputClass} />
            </div>
          </div>
        </details>

        {/* Published */}
        <div className="flex items-center justify-between py-3 border-t border-dark-border">
          <div>
            <p className="text-sm font-medium text-dark-text">Published</p>
            <p className="text-xs text-dark-text-secondary">
              {form.published ? 'Visible on all sites' : 'Draft — not visible publicly'}
            </p>
          </div>
          <button
            onClick={() => set('published', !form.published)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.published ? 'bg-blue-600' : 'bg-slate-600'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.published ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm">{error}</div>
        )}

        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition disabled:opacity-50"
        >
          {saving ? 'Saving...' : isNew ? 'Create Product' : 'Save Changes'}
        </button>

        {!isNew && (
          <button onClick={handleDelete} className="w-full py-2 text-red-400 hover:text-red-300 text-sm transition">
            Delete product
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function ProductsPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterBrand, setFilterBrand] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'published' | 'draft'>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Product | null | 'new'>(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildMsg, setRebuildMsg] = useState('');

  const handleRebuild = async () => {
    setRebuilding(true);
    setRebuildMsg('');
    try {
      const res = await fetch(getApiUrl('/api/rebuild'), {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      setRebuildMsg(res.ok ? (data.message || 'Rebuild triggered.') : (data.error || 'Rebuild failed.'));
    } catch {
      setRebuildMsg('Network error triggering rebuild.');
    } finally {
      setRebuilding(false);
      setTimeout(() => setRebuildMsg(''), 8000);
    }
  };

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (filterBrand) params.set('brand', filterBrand);
      if (search) params.set('search', search);

      const res = await fetch(getApiUrl(`/api/products/admin/all?${params}`), {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setProducts(data.products || []);
      }
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [filterBrand, search]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const filtered = products.filter((p) => {
    if (filterStatus === 'published' && p.published !== 1) return false;
    if (filterStatus === 'draft' && p.published !== 0) return false;
    return true;
  });

  const handlePanelSaved = () => {
    setSelected(null);
    fetchProducts();
  };

  const panelOpen = selected !== null;

  return (
    <div className="flex h-full gap-0">
      <div className={`flex flex-col flex-1 min-w-0 transition-all duration-200 ${panelOpen ? 'hidden lg:flex' : 'flex'}`}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Products</h1>
            <p className="text-dark-text-secondary text-sm mt-1">
              Manage product pages across all sites. Use AI to auto-populate from manufacturer URLs.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {rebuildMsg && (
              <span className="text-xs text-dark-text-secondary max-w-[220px]">{rebuildMsg}</span>
            )}
            <button
              onClick={handleRebuild}
              disabled={rebuilding}
              title="Rebuild and redeploy all sites so published changes go live"
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium rounded-lg transition disabled:opacity-50 whitespace-nowrap"
            >
              {rebuilding ? 'Triggering…' : 'Rebuild Sites'}
            </button>
            <button onClick={() => setSelected('new')} className="btn btn-primary whitespace-nowrap">
              + New Product
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mb-4">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products..."
            className="px-3 py-2 bg-dark-surface border border-dark-border rounded-lg text-dark-text text-sm focus:outline-none focus:border-blue-500 w-48"
          />
          <select
            value={filterBrand}
            onChange={(e) => setFilterBrand(e.target.value)}
            className="px-3 py-2 bg-dark-surface border border-dark-border rounded-lg text-dark-text text-sm focus:outline-none focus:border-blue-500"
          >
            {BRANDS.map((b) => <option key={b.value} value={b.value}>{b.value === '' ? 'All Brands' : b.label}</option>)}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as typeof filterStatus)}
            className="px-3 py-2 bg-dark-surface border border-dark-border rounded-lg text-dark-text text-sm focus:outline-none focus:border-blue-500"
          >
            <option value="all">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
          <span className="text-dark-text-secondary text-sm self-center">
            {filtered.length} product{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div className="bg-dark-surface rounded-xl border border-dark-border overflow-hidden flex-1">
          {loading ? (
            <div className="p-8 text-center text-dark-text-secondary">Loading products...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-dark-text-secondary mb-4">No products found.</p>
              <button onClick={() => setSelected('new')} className="btn btn-primary">
                Create your first product
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-dark-border">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-dark-text-secondary uppercase tracking-wide w-12" />
                    <th className="text-left px-4 py-3 text-xs font-semibold text-dark-text-secondary uppercase tracking-wide">Product</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-dark-text-secondary uppercase tracking-wide hidden md:table-cell">Type</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-dark-text-secondary uppercase tracking-wide hidden sm:table-cell">Format</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-dark-text-secondary uppercase tracking-wide hidden lg:table-cell">Brand</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-dark-text-secondary uppercase tracking-wide">Status</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-dark-border">
                  {filtered.map((product) => (
                    <tr
                      key={product.id}
                      className="hover:bg-slate-700/30 transition-colors cursor-pointer"
                      onClick={() => setSelected(product)}
                    >
                      <td className="px-4 py-3">
                        {product.primary_image_url ? (
                          <img src={product.primary_image_url} alt="" className="w-10 h-10 object-contain rounded bg-white p-0.5" />
                        ) : (
                          <div className="w-10 h-10 rounded bg-slate-700 flex items-center justify-center text-slate-500 text-xs">N/A</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-dark-text font-medium text-sm">{product.manufacturer} {product.model_name}</p>
                        <p className="text-dark-text-secondary text-xs font-mono mt-0.5">{product.slug}</p>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-dark-text-secondary text-sm uppercase">{product.product_type}</span>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className="text-dark-text-secondary text-sm">{product.format || '-'}</span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="text-dark-text-secondary text-sm">
                          {product.brand_id
                            ? BRANDS.find((b) => b.value === product.brand_id)?.label ?? product.brand_id
                            : 'All Brands'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          product.published ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-slate-400'
                        }`}>
                          {product.published ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelected(product); }}
                          className="text-blue-400 hover:text-blue-300 text-sm font-medium transition"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {panelOpen && (
        <div className="flex flex-col w-full lg:w-[520px] lg:ml-6 bg-dark-surface rounded-xl border border-dark-border overflow-hidden flex-shrink-0">
          <ProductPanel
            product={selected === 'new' ? null : selected}
            onSaved={handlePanelSaved}
            onClose={() => setSelected(null)}
          />
        </div>
      )}
    </div>
  );
}
